import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useOutletContext } from "react-router-dom";
import { toast } from "react-toastify";
import Seo from "../../components/Seo";
import { PageHeader, PickerField } from "../../components/lawyer/LawyerUi";
import StripeCardModal from "../../components/user/StripeCardModal";
import { apiErrorMessage, userService } from "../../services/userApi";

const MAX_FILES = 2;

// Post a question to lawyers. When the admin charges for questions
// (profile.question_payment) the card is taken before the question is posted.
const UserAskQuestion = () => {
  const navigate = useNavigate();
  const { profile } = useOutletContext();
  const fileRef = useRef(null);
  const [jurisdictions, setJurisdictions] = useState([]);
  const [jurisdictionId, setJurisdictionId] = useState("");
  const [question, setQuestion] = useState("");
  const [files, setFiles] = useState([]);
  const [accepted, setAccepted] = useState(false);
  const [saving, setSaving] = useState(false);
  const [payOpen, setPayOpen] = useState(false);

  const price = Number(profile?.question_payment) || 0;
  const howItWorks = profile?.question_how_works || [];

  useEffect(() => {
    userService
      .getDropdownData()
      .then((res) => {
        const list = res?.data?.data?.jurisdictions || [];
        setJurisdictions(list.map((j) => ({ value: String(j.id), label: j.name })));
      })
      .catch(() => toast.error("Couldn't load jurisdictions."));
  }, []);

  const ready = question.trim() && jurisdictionId && accepted;

  const post = async () => {
    const res = await userService.addQuestion({ question: question.trim(), jurisdictionId, attachments: files });
    if (!res?.data?.status) throw new Error(res?.data?.message || "Unable to post your question.");
    toast.success(res.data.message || "Question posted successfully!");
    navigate(res.data.data?.id ? `/user/requests/${res.data.data.id}` : "/user/requests", { replace: true });
    return true;
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!ready) return;
    if (price > 0) {
      setPayOpen(true);
      return;
    }
    setSaving(true);
    try {
      await post();
    } catch (err) {
      toast.error(apiErrorMessage(err, err?.message));
    } finally {
      setSaving(false);
    }
  };

  const pickFiles = (e) => {
    const picked = Array.from(e.target.files || []);
    e.target.value = "";
    const tooBig = picked.find((f) => f.size > 10 * 1024 * 1024);
    if (tooBig) {
      toast.error("Files must be 10 MB or smaller.");
      return;
    }
    setFiles((prev) => [...prev, ...picked].slice(0, MAX_FILES));
  };

  return (
    <div className="lwp-page usp-vc">
      <Seo title="Ask a Question | Legal Platform" noindex />
      <PageHeader title="Create New Question" backTo="/user/requests" />

      {howItWorks.length > 0 && (
        <ul className="usp-how">
          {howItWorks.map((line) => (
            <li key={line}>
              <i className="bi bi-check-circle-fill" /> {line}
            </li>
          ))}
        </ul>
      )}

      <form onSubmit={submit}>
        <section className="usp-vc-card">
          <h3>
            1. Choose Jurisdiction <span className="usp-req">*</span>
          </h3>
          <PickerField
            label="Jurisdiction"
            hideLabel
            placeholder="Select Jurisdiction"
            options={jurisdictions}
            value={jurisdictionId}
            onChange={setJurisdictionId}
          />
        </section>

        <section className="usp-vc-card">
          <h3>
            2. Your question <span className="usp-req">*</span>
          </h3>
          <textarea
            className="usp-textarea"
            placeholder="Describe your legal question in detail..."
            maxLength={2000}
            value={question}
            onChange={(e) => setQuestion(e.target.value)}
          />
        </section>

        <section className="usp-vc-card">
          <h3>3. Attachments (optional)</h3>
          {files.length > 0 && (
            <ul className="usp-file-list">
              {files.map((f, i) => (
                <li key={f.name + i}>
                  <i className="bi bi-paperclip" />
                  <span>{f.name}</span>
                  <button
                    type="button"
                    className="lwp-icon-btn"
                    onClick={() => setFiles((prev) => prev.filter((_, k) => k !== i))}
                    aria-label={`Remove ${f.name}`}
                  >
                    <i className="bi bi-x-lg" />
                  </button>
                </li>
              ))}
            </ul>
          )}
          {files.length < MAX_FILES && (
            <button type="button" className="lwp-pill-btn" onClick={() => fileRef.current?.click()}>
              <i className="bi bi-upload" />
              <span>Add document</span>
            </button>
          )}
          <input ref={fileRef} type="file" hidden multiple accept=".pdf,.doc,.docx,image/*" onChange={pickFiles} />
        </section>

        <label className="lwp-check">
          <input type="checkbox" checked={accepted} onChange={(e) => setAccepted(e.target.checked)} />
          <span>
            Accept all <Link to="/privacy" target="_blank">Privacy policy</Link> and{" "}
            <Link to="/terms" target="_blank">Terms &amp; conditions</Link>.
          </span>
        </label>

        <button type="submit" className="lwp-btn usp-vc-cta" disabled={!ready || saving}>
          {saving ? (
            <span className="spinner-border spinner-border-sm" />
          ) : price > 0 ? (
            `Pay $ ${price.toFixed(2)} & Post`
          ) : (
            "Post Question"
          )}
        </button>
      </form>

      <StripeCardModal
        open={payOpen}
        onClose={() => setPayOpen(false)}
        title="Post Question"
        amountLabel={`$ ${price.toFixed(2)}`}
        submitLabel={`Pay $ ${price.toFixed(2)}`}
        billing={{ name: profile?.name, email: profile?.email }}
        prepare={() => userService.questionPayment(price)}
        onPaid={post}
      />
    </div>
  );
};

export default UserAskQuestion;
